import PlusIcon from "@redotech/redo-web/icon/plus.svg";
import TrashIcon from "@redotech/redo-web/icon/trash.svg";
import { Input } from "@redotech/ui/form";
import * as classnames from "classnames";
import {
  ForwardedRef,
  ReactNode,
  forwardRef,
  memo,
  useId,
  useState,
} from "react";
import { InputSize } from "./input";
import { LabelTheme, LabeledInput } from "./labeled-input";
import * as textInputCss from "./text-input.module.css";

export type InputTheme =
  | typeof InputTheme.ACCENT
  | typeof InputTheme.FORM
  | typeof InputTheme.WIDGET;

export namespace InputTheme {
  export const ACCENT = Symbol("accent");
  export const FORM = Symbol("form");
  export const WIDGET = Symbol("widget");
}

const inputThemeClass = new Map<InputTheme, string>();
inputThemeClass.set(InputTheme.ACCENT, textInputCss.accent);
inputThemeClass.set(InputTheme.FORM, textInputCss.form);
inputThemeClass.set(InputTheme.WIDGET, textInputCss.widget);

export enum InputLines {
  SINGLE,
  MULTI,
}

const inputSizeClass = new Map<InputSize, string>();
inputSizeClass.set(InputSize.SMALL, textInputCss.small);
inputSizeClass.set(InputSize.MEDIUM, textInputCss.medium);

export enum InputBoxHeight {
  SHORT,
  MEDIUM,
  TALL,
}

const inputBoxHeightClass = new Map<InputBoxHeight, string>();
inputBoxHeightClass.set(InputBoxHeight.SHORT, textInputCss.shortHeight);
inputBoxHeightClass.set(InputBoxHeight.MEDIUM, textInputCss.mediumHeight);
inputBoxHeightClass.set(InputBoxHeight.TALL, textInputCss.tallHeight);

export interface TextInputProps {
  id?: string;
  error?: boolean;
  placeholder?: string;
  lines?: InputLines;
  icon?: ReactNode;
  autocomplete?: string;
  readonly?: boolean;
  size?: InputSize;
  suffix?: ReactNode;
  prefix?: ReactNode;
  theme?: InputTheme;
  type?: string;
  value: string;
  min?: number;
  max?: number;
  maxLength?: number;
  onChange?(value: string): void;
  onFocus?(value: boolean): void;
  onKeyDown?(
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ): void;
  fullwidth?: boolean;
  step?: number | string;
  autoFocus?: boolean;
  textAreaHeight?: InputBoxHeight;
  button?: ReactNode;
  smallText?: boolean;
  disabled?: boolean;
  showBorder?: boolean;
  verticalResizeOnly?: boolean;
}

export const TextInput = memo(
  forwardRef(
    (
      {
        error = false,
        id,
        lines = InputLines.SINGLE,
        suffix,
        size = InputSize.MEDIUM,
        placeholder,
        theme = InputTheme.WIDGET,
        autocomplete,
        prefix,
        readonly = false,
        icon,
        min,
        max,
        maxLength,
        value,
        type,
        onFocus,
        onChange,
        onKeyDown,
        fullwidth = false,
        step = 1,
        autoFocus = false,
        textAreaHeight = InputBoxHeight.SHORT,
        button,
        smallText = false,
        disabled = false,
        showBorder = true,
        verticalResizeOnly = false,
      }: TextInputProps,
      ref: ForwardedRef<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      return (
        <label
          className={classnames(
            textInputCss.textInput,
            inputThemeClass.get(theme),
            inputSizeClass.get(size),
            { [textInputCss.error]: error },
            { [textInputCss.noBorder]: !showBorder },
            { [textInputCss.disabled]: disabled },
          )}
          style={{
            flex: fullwidth ? 1 : undefined,
            width: fullwidth ? "100%" : undefined,
          }}
        >
          {icon && <div className={textInputCss.icon}>{icon}</div>}
          {prefix}
          {lines === InputLines.SINGLE ? (
            <input
              ref={ref as ForwardedRef<HTMLInputElement>}
              autoFocus={autoFocus}
              autoComplete={autocomplete}
              id={id}
              className={
                smallText
                  ? classnames(textInputCss.input, textInputCss.small)
                  : textInputCss.input
              }
              placeholder={placeholder}
              onChange={(event) => onChange && onChange(event.target.value)}
              onBlur={onFocus && (() => onFocus(false))}
              onFocus={onFocus && (() => onFocus(true))}
              onKeyDown={onKeyDown}
              max={max}
              min={min}
              readOnly={readonly}
              style={{ textAlign: suffix ? "right" : undefined }}
              type={type}
              value={value}
              step={type === "number" ? step : undefined}
              maxLength={maxLength}
              disabled={disabled}
            />
          ) : (
            <textarea
              ref={ref as ForwardedRef<HTMLTextAreaElement>}
              className={classnames(
                textInputCss.input,
                inputBoxHeightClass.get(textAreaHeight),
                { [textInputCss.vertical]: verticalResizeOnly },
              )}
              placeholder={placeholder}
              onChange={(event) => onChange && onChange(event.target.value)}
              value={value}
              maxLength={maxLength}
              onBlur={onFocus && (() => onFocus(false))}
              onFocus={onFocus && (() => onFocus(true))}
              onKeyDown={onKeyDown}
              readOnly={readonly}
              disabled={disabled}
            />
          )}
          {suffix}
          {button}
        </label>
      );
    },
  ),
);

export const FormTextInput = memo(
  ({
    description,
    label,
    input,
    labelTheme,
    lines,
    errorOveride = false,
    ...props
  }: {
    description?: ReactNode;
    labelTheme?: LabelTheme;
    label: ReactNode;
    input: Input<string>;
    errorOveride?: boolean;
    lines?: number;
  } & Omit<
    TextInputProps,
    "error" | "id" | "value" | "onChange" | "onFocus"
  >) => {
    const [focus, setFocus] = useState(false);
    const id = useId();
    return (
      <LabeledInput
        description={description}
        label={label}
        theme={labelTheme}
        errors={input.changed && !focus ? input.errors : []}
        id={id}
      >
        <TextInput
          id={id}
          error={
            errorOveride
              ? true
              : input.changed && !focus && !!input.errors.length
          }
          value={input.value}
          onChange={input.setValue}
          onFocus={setFocus}
          lines={lines}
          {...props}
        ></TextInput>
      </LabeledInput>
    );
  },
);

export const FormTextListInput = memo(
  ({
    description,
    label,
    input,
    labelTheme,
    lines,
    readonly = false,
    ...props
  }: {
    description?: ReactNode;
    labelTheme?: LabelTheme;
    label: ReactNode;
    input: Input<string[]>;
    readonly?: boolean;
  } & Omit<
    TextInputProps,
    "error" | "id" | "value" | "onChange" | "onFocus"
  >) => {
    const [focus, setFocus] = useState(false);
    const id = useId();

    const handleChange = (index: number, value: string) => {
      const newValue = [...input.value];
      newValue[index] = value;
      input.setValue(newValue);
    };

    const handleDelete = (index: number) => {
      const newValue = [...input.value];
      newValue.splice(index, 1);
      input.setValue(newValue);
    };

    const handleAdd = () => {
      const newValue = [...input.value];
      newValue.push("");
      input.setValue(newValue);
    };

    return (
      <LabeledInput
        description={description}
        label={label}
        theme={labelTheme}
        errors={input.changed && !focus ? input.errors : []}
        id={id}
      >
        <>
          {input.value.map((value, index) => (
            <div key={index} className={textInputCss.listItem}>
              <div>{index + 1}.</div>
              <TextInput
                readonly={readonly}
                id={id}
                error={input.changed && !focus && !!input.errors.length}
                value={value}
                onChange={(e) => handleChange(index, e)}
                onFocus={setFocus}
                lines={lines}
                fullwidth={true}
                {...props}
              ></TextInput>
              {!readonly && (
                <div
                  className={textInputCss.deleteIcon}
                  onClick={() => handleDelete(index)}
                >
                  <TrashIcon />
                </div>
              )}
            </div>
          ))}
          {!readonly && (
            <div onClick={handleAdd} className={textInputCss.add}>
              <div className={textInputCss.addIcon}>
                <PlusIcon />
              </div>
              Add
            </div>
          )}
        </>
      </LabeledInput>
    );
  },
);
